//frontend/src/components/AdminRequests/Chat/components/MessageBubble.jsx

import React, { useEffect, useRef, useState } from "react";
import {
  fetchMediaBlobUrl,
  downloadMedia,
} from "../utils/media";

const REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🔥"];
const MEDIA_TYPES = new Set(["m.image", "m.video", "m.audio"]);

// Если URL уже готов к использованию (blob:, data: и т.п.) — возвращаем его
// напрямую. fetchMediaBlobUrl нужен только для mxc:// (требует авторизации
// и преобразования в blob-URL).
function resolveDirectMediaUrl(url, msgtype) {
  if (!url) return null;
  if (!MEDIA_TYPES.has(msgtype)) return null;
  if (url.startsWith("mxc://")) return null;
  return url;
}

export default function MessageBubble({
  event,
  isMine,
  accessToken,
  senderName,
  senderAvatar,
  reactions = [],
  repliedTo = null,
  onReply,
  onReact,
}) {
  const content = event.content || {};
  const msgtype = content.msgtype;
  const body = content.body || "";

  // Инициализируем состоянием сразу из blob:/data: URL, чтобы не мигать
  // «Загрузкой» при remount (tempId → server event_id).
  const [mediaUrl, setMediaUrl] = useState(() =>
    resolveDirectMediaUrl(content.url, msgtype)
  );
  const [mediaErr, setMediaErr] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const holderRef = useRef(null);

  useEffect(() => {
    // 1) Локальные (blob:/data:) URL — показываем сразу, без сети
    const direct = resolveDirectMediaUrl(content.url, msgtype);
    if (direct) {
      setMediaUrl(direct);
      setMediaErr(false);
      return;
    }

    // 2) Нет URL или не медиа — сбрасываем
    setMediaUrl(null);
    setMediaErr(false);
    if (!content.url) return;
    if (!MEDIA_TYPES.has(msgtype)) return;

    // 3) mxc:// — тянем через Matrix media API
    let alive = true;
    fetchMediaBlobUrl({
      mxc: content.url,
      accessToken,
      thumb: msgtype === "m.image" || msgtype === "m.video",
    })
      .then((url) => {
        if (alive) setMediaUrl(url);
      })
      .catch(() => {
        if (alive) setMediaErr(true);
      });

    return () => {
      alive = false;
    };
  }, [content.url, msgtype, accessToken]);

  const handleDownloadFile = () => {
    if (!content.url) return;

    if (content.url.startsWith("mxc://")) {
      downloadMedia({
        mxc: content.url,
        accessToken,
        filename: body,
      });
      return;
    }

    // blob:/data:/http(s): — скачиваем напрямую
    const a = document.createElement("a");
    a.href = content.url;
    a.download = body || "file";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const replyEventId =
    content["m.relates_to"]?.["m.in_reply_to"]?.event_id || null;

  return (
    <div
      ref={holderRef}
      className={`chat-msg ${isMine ? "chat-msg--mine" : "chat-msg--other"}`}
    >
      {!isMine && (
        <div className="chat-msg__avatar">
          {senderAvatar ? (
            <img src={senderAvatar} alt={senderName} />
          ) : (
            <span>{(senderName || "?").slice(0, 1).toUpperCase()}</span>
          )}
        </div>
      )}

      <div className="chat-msg__body">
        {!isMine && (
          <div className="chat-msg__sender">{senderName || "Участник"}</div>
        )}

        {repliedTo && (
          <div className="chat-msg__reply">
            <div className="chat-msg__reply-author">
              {repliedTo.senderName || "Сообщение"}
            </div>
            <div className="chat-msg__reply-text">{repliedTo.preview}</div>
          </div>
        )}

        <div className="chat-msg__bubble">
          {msgtype === "m.text" && (
            <div className="chat-msg__text">{body}</div>
          )}

          {msgtype === "m.image" && (
            <div className="chat-msg__media">
              {mediaUrl ? (
                <img src={mediaUrl} alt={body} />
              ) : (
                <div className="chat-msg__media-placeholder">
                  {mediaErr ? "Не удалось загрузить" : "Загрузка…"}
                </div>
              )}
            </div>
          )}

          {msgtype === "m.video" && (
            <div className="chat-msg__media">
              {mediaUrl ? (
                <video src={mediaUrl} controls />
              ) : (
                <div className="chat-msg__media-placeholder">
                  {mediaErr ? "Не удалось загрузить" : "Загрузка…"}
                </div>
              )}
            </div>
          )}

          {msgtype === "m.audio" && (
            <div className="chat-msg__audio">
              {mediaUrl ? (
                <audio src={mediaUrl} controls />
              ) : (
                <div className="chat-msg__media-placeholder">
                  {mediaErr ? "Не удалось загрузить" : "Загрузка…"}
                </div>
              )}
            </div>
          )}

          {msgtype === "m.file" && (
            <button
              type="button"
              className="chat-msg__file"
              onClick={handleDownloadFile}
              title="Скачать"
            >
              📎 <span>{body}</span>
            </button>
          )}
        </div>

        {reactions.length > 0 && (
          <div className="chat-msg__reactions">
            {reactions.map((r) => (
              <span key={r.key} className="chat-msg__reaction">
                {r.key} {r.count > 1 ? r.count : ""}
              </span>
            ))}
          </div>
        )}

        <div className="chat-msg__actions">
          <button
            type="button"
            className="chat-msg__action-btn"
            title="Ответить"
            onClick={() => onReply?.(event)}
          >
            ↩
          </button>
          <button
            type="button"
            className="chat-msg__action-btn"
            title="Реакция"
            onClick={() => setShowReactions((v) => !v)}
          >
            😊
          </button>
          {showReactions && (
            <div className="chat-msg__reaction-picker">
              {REACTION_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => {
                    onReact?.(event, emoji);
                    setShowReactions(false);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}